import { ICONS } from '@frontend/icons';
import React from 'react';

import { RequiredFlag } from '../required-flag';
import useMultiTextArea from './multi-text-area.controller';

export type ValidationErrorType = 'duplicate' | 'invalid' | 'required';

export interface MultiTextAreaErrorMessages {
    /**
     * Error message when a duplicate value is entered.
     */
    duplicate?: React.ReactNode;
    /**
     * Error message when validation fails for the entered value.
     */
    invalid?: React.ReactNode;
    /**
     * Error message when at least one value is required but none are provided.
     */
    required?: React.ReactNode;
}

export interface MultiTextAreaProps {
    id?: string;
    required?: boolean;
    valid?: boolean;
    isValidCallback?: (valid: boolean) => void;
    onChange?: (values: string[]) => void;
    values?: string[];
    /**
     * @deprecated Use errorMessages.required instead
     */
    errorMessage?: React.ReactNode;
    label?: React.ReactNode;
    helpMessage?: React.ReactNode;
    placeholder?: string;
    submitted?: boolean;
    className?: string;
    /**
     * Custom validation function for each input value.
     * Return true if valid, false if invalid.
     */
    validateValue?: (value: string) => boolean;
    /**
     * @deprecated Use errorMessages.invalid instead
     */
    validationErrorMessage?: React.ReactNode;
    /**
     * Custom error messages for different validation error types.
     */
    errorMessages?: MultiTextAreaErrorMessages;
}

const DEFAULT_ERROR_MESSAGES: Record<ValidationErrorType, React.ReactNode> = {
    duplicate: 'Value already exists',
    invalid: 'Invalid value',
    required: 'At least one value is required'
};

export const MultiTextArea = (props: MultiTextAreaProps) => {
    const {
        inputValue,
        touched,
        validationErrorType,
        inputRef,
        containerRef,
        values,
        isValid,
        removeValue,
        handleKeyDown,
        handleBlur,
        handleContainerClick,
        handleInputChange,
        handleFocus
    } = useMultiTextArea(props);

    const getErrorMessage = (errorType: ValidationErrorType): React.ReactNode => {
        // Check new errorMessages prop first
        if (props.errorMessages?.[errorType]) {
            return props.errorMessages[errorType];
        }
        // Fall back to deprecated props for backward compatibility
        if (errorType === 'invalid' && props.validationErrorMessage) {
            return props.validationErrorMessage;
        }
        if (errorType === 'required' && props.errorMessage) {
            return props.errorMessage;
        }
        // Use default messages
        return DEFAULT_ERROR_MESSAGES[errorType];
    };

    return (
        <div className={'form-group ' + (props.className ?? '')}>
            {props.label && (
                <div className='mb-0! inline-flex w-full items-center gap-1'>
                    <span className='text-foreground-default text-sm font-medium'>{props.label}</span>
                    {props.helpMessage && (
                        <>
                            <br />
                            <small className='form-text text-muted'>{props.helpMessage}</small>
                        </>
                    )}
                    <RequiredFlag required={props.required} />
                </div>
            )}
            <div
                ref={containerRef}
                onClick={handleContainerClick}
                className={`border-border-default bg-background-surface flex min-h-[100px] cursor-text flex-wrap content-start items-start gap-2 overflow-auto rounded-md border p-4 ${
                    touched && !isValid ? 'border-status-error' : ''
                }`}>
                {values.map((value, index) => (
                    <div
                        key={`${value}-${index}`}
                        className='bg-border-default flex h-[25px] items-center gap-1 rounded px-1'>
                        <span className='text-foreground-default text-sm font-medium'>{value}</span>
                        <ICONS.BUTTON.CLOSE
                            className='text-foreground-default size-4 cursor-pointer'
                            onClick={(e) => {
                                e.stopPropagation();
                                removeValue(index);
                            }}
                        />
                    </div>
                ))}
                <input
                    ref={inputRef}
                    type='text'
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    placeholder={values.length === 0 ? props.placeholder : ''}
                    className='text-foreground-default min-w-[100px] flex-1 border-none bg-transparent text-sm leading-[25px] font-medium outline-none'
                />
            </div>
            {validationErrorType && (
                <span className='text-status-danger mt-2 font-medium'>
                    <small>{getErrorMessage(validationErrorType)}</small>
                </span>
            )}
            {touched && !isValid && props.required && !validationErrorType && (
                <span className='text-status-danger mt-2 font-medium'>
                    <small>{getErrorMessage('required')}</small>
                </span>
            )}
        </div>
    );
};

export default MultiTextArea;
